import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import * as Lucide from "lucide-react";
import { optimg } from "../lib/img";

export function DynamicIcon({ name, ...props }) {
  const Cmp = Lucide[name] || Lucide.Building2;
  return <Cmp {...props} />;
}

export function Button({ children, to, href, variant = "primary", className = "", icon = true, ...props }) {
  const base = "group inline-flex items-center justify-center gap-2 font-semibold text-sm rounded-full px-6 py-3 transition-all duration-300 ease-premium select-none";
  const styles = {
    primary: "bg-gold text-white hover:bg-gold-dark shadow-gold hover:-translate-y-0.5",
    dark: "bg-ink text-white hover:bg-black hover:-translate-y-0.5",
    outline: "border border-line text-ink bg-white hover:border-gold hover:text-gold",
    ghost: "text-ink hover:text-gold",
    light: "bg-white/10 text-white border border-white/25 hover:bg-white/20",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  const inner = (
    <>
      {children}
      {icon && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
    </>
  );
  if (to) return <Link to={to} className={cls} {...props}>{inner}</Link>;
  if (href) return <a href={href} className={cls} {...props}>{inner}</a>;
  return <button className={cls} {...props}>{inner}</button>;
}

export function Reveal({ children, delay = 0, className = "", as: Tag = "div" }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setSeen(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <Tag ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`reveal ${seen ? "in" : ""} ${className}`}>
      {children}
    </Tag>
  );
}

export function SmartImage({ src, alt = "", className = "", imgClass = "", eager = false, w, h, natural = false, ...props }) {
  const finalSrc = optimg(src, w || h ? { w, h } : {});
  if (natural) {
    return (
      <img
        src={finalSrc}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        referrerPolicy="no-referrer"
        className={`block w-full h-auto ${imgClass} ${className}`}
        {...props}
      />
    );
  }
  return (
    <div className={`absolute inset-0 overflow-hidden bg-beige ${className}`}>
      <img
        src={finalSrc}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        referrerPolicy="no-referrer"
        className={`w-full h-full object-cover ${imgClass}`}
        {...props}
      />
    </div>
  );
}

export function SectionHeading({ kicker, title, subtitle, center = false, light = false }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      {kicker && <div className="kicker mb-3">{kicker}</div>}
      <h2 className={`text-3xl md:text-[2.6rem] leading-[1.1] font-bold ${light ? "text-white" : "text-ink"}`}>{title}</h2>
      {subtitle && <p className={`mt-4 text-base md:text-lg ${light ? "text-white/70" : "text-muted"}`}>{subtitle}</p>}
    </div>
  );
}

export function Counter({ value }) {
  // Parse leading number, keep suffix (e.g., 500+, End to End)
  const match = String(value).match(/^(\d+)(.*)$/);
  const ref = useRef(null);
  const [display, setDisplay] = useState(match ? "0" + match[2] : value);
  useEffect(() => {
    if (!match) return;
    const target = parseInt(match[1], 10);
    const suffix = match[2];
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setDisplay(target + suffix); return; }
    const el = ref.current;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const dur = 1400; const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(Math.round(target * eased) + suffix);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{display}</span>;
}
